"use client";

/**
 * Búsqueda 2.5 / 2.6 — buscador en vivo por nombre, ingrediente o categoría,
 * con chips de búsquedas frecuentes y estado vacío.
 */

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { FotoBoot } from "@/components/app/foto-boot";
import { PrecioDist } from "@/components/app/precio-dist";
import { useApp } from "@/components/app/proveedor";
import { IconoLupa } from "@/components/app/iconos";
import type { ProductoApp } from "@/components/app/tipos";

const FRECUENTES = ["Jengibre", "Sin azúcar", "Post entreno", "Detox"];

export function BuscarCliente({ productos }: { productos: ProductoApp[] }) {
  const router = useRouter();
  const { descuentoDist } = useApp();
  const [q, setQ] = useState("");

  const resultados = useMemo(() => {
    const texto = q.trim().toLowerCase();
    if (!texto) return productos;
    return productos.filter((p) =>
      `${p.nombre} ${p.ingredientes.join(" ")} ${p.categoria} ${p.descripcion}`
        .toLowerCase()
        .includes(texto),
    );
  }, [q, productos]);

  return (
    <>
      <HeaderTitulo titulo="Buscar" />
      <main className="anim-bz-in min-h-(--alto-app) px-[17px] pt-[100px] pb-[62px]">
        <div className="pt-5 pb-[30px]">
          <div className="flex h-[52px] items-center gap-2.5 rounded-[26px] bg-humo px-5">
            <IconoLupa color="rgba(34,31,31,.5)" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="mango, digestión, energía…"
              autoFocus
              className="min-w-0 flex-1 border-0 bg-transparent text-base text-tinta outline-0 placeholder:text-tinta/50"
            />
          </div>

          <p className="mt-5 text-[13px] text-tinta/50">Búsquedas frecuentes</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {FRECUENTES.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setQ(f)}
                className="flex h-[34px] items-center rounded-[18px] border border-black/10 bg-white px-3.5 text-[13px]"
              >
                {f}
              </button>
            ))}
          </div>

          {resultados.length > 0 ? (
            <>
              <p className="mt-6 text-[13px] text-tinta/50">
                {resultados.length} resultados
              </p>
              <div className="mt-3 flex flex-col gap-3">
                {resultados.map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => router.push(`/productos/${p.slug}`)}
                    className="flex items-center gap-3.5 rounded-3xl border border-black/5 bg-white p-3 text-left"
                  >
                    <span
                      className="h-[72px] w-[72px] flex-none overflow-hidden rounded-[18px]"
                      style={{ background: p.gradiente }}
                    >
                      <FotoBoot imagen={p.imagen} gradiente={p.gradiente} alt={p.nombre} sizes="72px" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-app text-base font-semibold text-tinta">
                        {p.nombre}
                      </span>
                      <span className="mt-[3px] block text-xs text-tinta/55">
                        {p.categoria} · 210 g
                      </span>
                    </span>
                    <span className="font-app text-base font-semibold">
                      <PrecioDist precio={p.precio} descuento={descuentoDist} claseTachado="text-xs" />
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="mt-[70px] flex flex-col items-center gap-3.5 text-center">
              <span className="flex h-[88px] w-[88px] items-center justify-center rounded-[30px] bg-humo">
                <IconoLupa size={38} color="rgba(34,31,31,.35)" strokeWidth={1.8} />
              </span>
              <h2 className="max-w-[260px] font-app text-2xl leading-7 font-black text-tinta">
                Eso todavía no lo congelamos
              </h2>
              <p className="max-w-[250px] text-sm leading-[21px] text-tinta/60">
                Pero tenemos {productos.length} boots que quizá te gusten más.
              </p>
              <button
                type="button"
                onClick={() => router.push("/productos")}
                className="mt-1.5 flex h-14 items-center rounded-[28px] bg-verde px-7 font-app text-base font-semibold text-white"
              >
                Ver el catálogo
              </button>
            </div>
          )}
        </div>
      </main>
      <BottomNav />
    </>
  );
}
